import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserAvatar } from '@/components/UserAvatar'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { ChevronDown, LogOut, Shield, User, Wrench } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export function UserMenu() {
  const { t } = useTranslation()
  const { currentUser, isAdmin, isMaintainer, isUser, isVendor, signout } = useAuth()

  function getRoleIcon() {
    if (isAdmin) return Shield
    if (isMaintainer) return Wrench
    return User
  }

  function getRoleLabel() {
    if (isAdmin) return t('users.roles.admin')
    if (isMaintainer) return t('users.roles.maintainer')
    if (isUser) return t('users.roles.user')
    if (isVendor) return t('users.roles.vendor')
    return ''
  }

  const RoleIcon = currentUser ? getRoleIcon() : User

  const handleLogout = () => {
    signout()
  }

  if (!currentUser) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-button" className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-sidebar-accent">
        <UserAvatar
          name={currentUser.name}
          username={currentUser.username}
          avatarId={currentUser.avatarId}
          className="size-8 bg-primary/20"
          fallbackClassName="text-xs font-bold text-primary"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-xs font-medium text-sidebar-foreground">{currentUser.name}</span>
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <RoleIcon className="size-2.5" />
            {getRoleLabel()}
          </span>
        </div>
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 bg-card text-foreground" side="top" align="start">
        <DropdownMenuLabel className="text-xs text-muted-foreground">{t('users.userInfo')}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="flex items-center gap-3 px-2 py-2">
          <UserAvatar
            name={currentUser.name}
            username={currentUser.username}
            avatarId={currentUser.avatarId}
            className="size-7 bg-primary/20"
            fallbackClassName="text-[10px] font-bold text-primary"
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-xs font-medium">{currentUser.name}</span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <RoleIcon className="size-2.5" />
              {getRoleLabel()}
            </span>
          </div>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={handleLogout}
          className="flex items-center gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <LogOut className="size-4" />
          <span>{t('users.logout')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
